// src/services/dataService.js
import { supabase } from '../lib/supabaseClient';

/**
 * Save or update business data
 * @param {Object} businessData - The business data to save
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - The saved business data
 */
export const saveBusinessData = async (businessData, userId) => {    
  try {    
    // Validate inputs  
    if (!userId) {  
      throw new Error("User ID is required");  
    }  
      
    if (!businessData) {  
      throw new Error("Business data is required");  
    }  
      
    // First save the business profile with UPSERT to handle conflicts    
    const { data: existingProfiles, error: fetchError } = await supabase
      .from('business_profiles')
      .select('*')
      .eq('user_id', userId);
    
    let businessProfileResult;
    
    // If the profile already exists, update it with known ID to avoid conflict  
    if (!fetchError && existingProfiles && existingProfiles.length > 0) {  
      businessProfileResult = await supabase      
        .from('business_profiles')      
        .update({      
          business_name: businessData.businessName || "",      
          industry: businessData.industry || "",
          // Don't overwrite first_name and last_name if they're not provided
          ...(businessData.firstName && { first_name: businessData.firstName }),
          ...(businessData.lastName && { last_name: businessData.lastName }),
          // ADD monthly_overhead here
          monthly_overhead: businessData.monthlyOverhead || {
            workspace: 0,
            adminStaff: 0,
            marketing: 0,
            software: 0,
            other: 0
          },
          onboarding_completed: businessData.onboardingCompleted || false,      
          updated_at: new Date().toISOString()      
        })  
        .eq('user_id', userId);  
    } else {  
      // Otherwise create a new one  
      businessProfileResult = await supabase      
        .from('business_profiles')      
        .insert({      
          user_id: userId,      
          business_name: businessData.businessName || "",      
          industry: businessData.industry || "",
          // Include first_name and last_name if provided
          ...(businessData.firstName && { first_name: businessData.firstName }),
          ...(businessData.lastName && { last_name: businessData.lastName }),
          // ADD monthly_overhead here
          monthly_overhead: businessData.monthlyOverhead || {
            workspace: 0,
            adminStaff: 0,
            marketing: 0,
            software: 0,
            other: 0
          },
          onboarding_completed: businessData.onboardingCompleted || false,      
          updated_at: new Date().toISOString()      
        });  
    }
      
    const businessError = businessProfileResult.error;  
      
    if (businessError) {  
      console.error("Error saving business profile:", businessError);  
      throw businessError;  
    }  

    // Then save each offer if there are any  
    if (businessData.offers && Array.isArray(businessData.offers) && businessData.offers.length > 0) {  
      
      // First, get all existing offers for this user to determine updates vs inserts  
      const existingOffersResult = await supabase  
        .from('offers')  
        .select('id, name')  
        .eq('user_id', userId);
      
      const existingOffers = existingOffersResult.data;
      const fetchError = existingOffersResult.error;
        
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error("Error fetching existing offers:", fetchError);
        throw fetchError;
      }

      const existingOfferMap = {};  
      if (existingOffers && Array.isArray(existingOffers)) {  
        existingOffers.forEach(offer => {  
          existingOfferMap[offer.name] = offer.id;  
        });  
      }

      // Prepare offers for upsert with proper validation
      const offersToUpsert = businessData.offers.map(offer => {
        // Add detailed validation and fallback values
        if (!offer.name) {
          console.warn("Found an offer without a name, using default");
        }
        
        // Ensure all offer properties exist and are valid
        const cleanOffer = {
          id: existingOfferMap[offer.name] || undefined, // Use existing ID if found  
          user_id: userId,  
          name: offer.name || "Unnamed Offer",  
          price: Number(offer.price) || 0,  
          quantity: Number(offer.quantity) || 1,  
          revenue: Number(offer.revenue) || (Number(offer.price || 0) * Number(offer.quantity || 1)),  
          total_costs: Number(offer.totalCosts) || 0,  
          profit: Number(offer.profit) || (Number(offer.revenue || 0) - Number(offer.totalCosts || 0)),  
          profit_margin: Number(offer.profitMargin) || 0,  
          // Convert nested objects to JSON strings, handling nulls  
          costs: JSON.stringify(offer.costs || {}),  
          team_members: JSON.stringify(offer.teamMembers || []),  
          direct_cost_items: JSON.stringify(offer.directCostItems || []),  
          fixed_monthly_time: JSON.stringify(offer.fixedMonthlyTime || []), // NEW
          overhead_percentage: Number(offer.overheadPercentage) || 0,  
          allocation_method: offer.allocationMethod || 'revenue',  
          monthly_overhead: JSON.stringify(offer.monthlyOverhead || {}),
          has_detailed_costs: offer.has_detailed_costs || false,
          updated_at: new Date().toISOString()
        };
        
        return cleanOffer;
      });

      // Upsert offers - try one by one if multiple offers are causing problems
      let hasErrors = false;
      
      for (let i = 0; i < offersToUpsert.length; i++) {
        const { error: offerError } = await supabase
          .from('offers')
          .upsert(offersToUpsert[i]);
          
        if (offerError) {
          console.error(`Error upserting offer ${offersToUpsert[i].name}:`, offerError);
          hasErrors = true;
          // Continue with other offers instead of breaking entirely
        }
      }
      
      if (hasErrors) {
        console.warn("Completed with some errors in offer upserts");
      }
    }

    const savedData = await getBusinessData(userId); 
    return savedData;  
  } catch (error) {  
    console.error('Error saving business data:', error);  
    throw error;  
  }  
};

/**
 * Get business data for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - The business data
 */
export const getBusinessData = async (userId) => {        
  try {        
    // Get business profile with proper error handling for duplicates  
    const { data: businessProfiles, error: businessError } = await supabase        
      .from('business_profiles')        
      .select('*')        
      .eq('user_id', userId)  
      .order('updated_at', { ascending: false });  // Get newest first

    // Check if no profile exists yet        
    if (businessError) {        
      console.error("Error fetching business profile:", businessError);  
      
      if (businessError.code === 'PGRST116') {        
        // This is a "no rows returned" error - normal for new users        
        // Return an empty structure        
        return {        
          businessName: '',        
          industry: '',
          monthlyOverhead: {
            workspace: 0,
            adminStaff: 0,
            marketing: 0,
            software: 0,
            other: 0
          },
          offers: []        
        };        
      } else {        
        // This is some other error        
        throw businessError;        
      }        
    }

    // If multiple profiles exist (shouldn't happen with constraint), use the most recent  
    if (businessProfiles && businessProfiles.length > 1) {  
      console.warn(`Found ${businessProfiles.length} business profiles for user ${userId}. Using most recent.`);  
    }

    // Handle case where query succeeded but no profiles were found  
    if (!businessProfiles || businessProfiles.length === 0) {  
      return {        
        businessName: '',        
        industry: '',
        monthlyOverhead: {
          workspace: 0,
          adminStaff: 0,
          marketing: 0,
          software: 0,
          other: 0
        },
        offers: []        
      };  
    }

    // Use the first (most recent) profile  
    const businessProfile = businessProfiles[0];

    // Get offers        
    const { data: offers, error: offersError } = await supabase        
      .from('offers')        
      .select(`
        id,
        user_id,
        name,
        price,
        quantity,
        revenue,
        total_costs,
        profit,
        profit_margin,
        costs,
        team_members,
        direct_cost_items,
        fixed_monthly_time,
        overhead_percentage,
        allocation_method,
        monthly_overhead,
        has_detailed_costs,
        updated_at
      `)        
      .eq('user_id', userId)        
      .order('updated_at', { ascending: false });

    if (offersError) {  
      console.error("Error fetching offers:", offersError);  
      throw offersError;  
    }  

    // Format the offers to match our application model        
    const formattedOffers = offers ? offers      
      .filter(offer => offer.name && offer.name.trim() !== '')      
      .map(offer => {    
        // Parse JSON fields with error handling    
        let costs = {};    
        let teamMembers = [];    
        let directCostItems = [];    
        let fixedMonthlyTime = []; // NEW
        let monthlyOverhead = {};    
            
        try {    
          costs = JSON.parse(offer.costs || '{}');    
        } catch (e) {    
          console.error(`Error parsing costs for offer ${offer.name}:`, e);    
        }    
            
        try {    
          teamMembers = JSON.parse(offer.team_members || '[]');    
        } catch (e) {    
          console.error(`Error parsing team_members for offer ${offer.name}:`, e);    
        }    
            
        try {    
          directCostItems = JSON.parse(offer.direct_cost_items || '[]');    
        } catch (e) {    
          console.error(`Error parsing direct_cost_items for offer ${offer.name}:`, e);    
        }    
        
        // NEW
        try {
          fixedMonthlyTime = JSON.parse(offer.fixed_monthly_time || '[]');
        } catch (e) {
          console.error(`Error parsing fixed_monthly_time for offer ${offer.name}:`, e);
        }
            
        try {    
          monthlyOverhead = JSON.parse(offer.monthly_overhead || '{}');    
        } catch (e) {    
          console.error(`Error parsing monthly_overhead for offer ${offer.name}:`, e);    
        }    
            
        // Extract values directly  
        const price = Number(offer.price || 0);  
        const totalCosts = Number(offer.total_costs || 0);  
        const profit = price - totalCosts;  
          
        // CRITICAL: Use the stored profit_margin directly if available
        // If not available or invalid, recalculate it
        let profitMargin;
        if (offer.profit_margin !== undefined && offer.profit_margin !== null) {
          profitMargin = Number(offer.profit_margin);
        } else {
          // Recalculate profit margin directly  
          profitMargin = price > 0 ? (profit / price) * 100 : 0;  
        }
            
        return {          
          name: offer.name,          
          price: offer.price,          
          quantity: offer.quantity,          
          revenue: offer.revenue,          
          totalCosts: offer.total_costs,          
          profit: offer.profit,          
          profitMargin: profitMargin,  
          costs: costs,          
          teamMembers: teamMembers,          
          directCostItems: directCostItems,
          fixedMonthlyTime: fixedMonthlyTime, // NEW
          overheadPercentage: offer.overhead_percentage,          
          allocationMethod: offer.allocation_method,          
          monthlyOverhead: monthlyOverhead,
          has_detailed_costs: offer.has_detailed_costs || false          
        };          
      }) : [];

    // Create final business data  
    const businessData = {          
      businessName: businessProfile.business_name || '',          
      industry: businessProfile.industry || '',
      onboardingCompleted: businessProfile.onboarding_completed || false,
      firstName: businessProfile.first_name || '',
      lastName: businessProfile.last_name || '',
      // ADD monthly_overhead from business profile
      monthlyOverhead: businessProfile.monthly_overhead || {
        workspace: 0,
        adminStaff: 0,
        marketing: 0,
        software: 0,
        other: 0
      },
      offers: formattedOffers          
    }; 
      
    return businessData;        
  } catch (error) {        
    console.error('Error getting business data:', error);  
    throw error;  // Re-throw to allow proper error handling upstream  
  }        
};

/**
 * Save monthly overhead values (business-wide)
 * @param {Object} overheadData - The overhead data to save
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} - Success indicator
 */
export const saveMonthlyOverhead = async (overheadData, userId) => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }

    const { error } = await supabase
      .from('business_profiles')
      .update({ 
        monthly_overhead: overheadData,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (error) {
      console.error("Error saving monthly overhead:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error saving monthly overhead:', error);
    return false;
  }
};

/**
 * Save a specific offer
 * @param {Object} offerData - The offer data to save
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - The saved offer
 */
export const saveOffer = async (offerData, userId) => {        
  try {        
    // Validate essential data    
    if (!offerData.name) {    
      console.error("Cannot save offer: Missing offer name");    
      return false;    
    }

    // CRITICAL FIX: Use the per-unit costs and revenue for margin calculation  
    // This matches how it's calculated in Cost Discovery  
      
    // First, extract all the values we need  
    const price = Number(offerData.price || 0);  
    const totalCosts = Number(offerData.totalCosts || 0);  
      
    // The profit is PER UNIT  
    const profit = price - totalCosts;  
      
    // Calculate the profit margin PER UNIT  
    let profitMargin = 0;  
    if (price > 0) {  
      profitMargin = (profit / price) * 100;  
    }  

    // Check if the offer already exists        
    const { data: existingOffer, error: fetchError } = await supabase        
      .from('offers')        
      .select('id')        
      .eq('user_id', userId)        
      .eq('name', offerData.name)        
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {        
      console.error("Error checking for existing offer:", fetchError);        
      throw fetchError;        
    }

    const offerToUpsert = {        
      id: existingOffer?.id,  
      user_id: userId,        
      name: offerData.name,        
      price: price,  
      quantity: Number(offerData.quantity || 1),  
      revenue: Number(offerData.revenue || 0),  
      total_costs: totalCosts,  
      profit: profit,  // This is per unit  
      profit_margin: profitMargin,  // This is the per unit percentage  
      costs: JSON.stringify(offerData.costs || {}),        
      team_members: JSON.stringify(offerData.teamMembers || []),        
      direct_cost_items: JSON.stringify(offerData.directCostItems || []),
      fixed_monthly_time: JSON.stringify(offerData.fixedMonthlyTime || []), // NEW
      overhead_percentage: Number(offerData.overheadPercentage || 0),        
      allocation_method: offerData.allocationMethod || 'revenue',        
      monthly_overhead: JSON.stringify(offerData.monthlyOverhead || {}),
      has_detailed_costs: offerData.has_detailed_costs || false,        
      updated_at: new Date().toISOString()        
    };

    const { error } = await supabase        
      .from('offers')        
      .upsert(offerToUpsert);

    if (error) {        
      console.error("Error during offer upsert:", error);        
      throw error;        
    }
      
    // CRITICAL: Return the offer with our corrected profit margin  
    const updatedOffer = {  
      ...offerData,  
      profit: profit,  
      profitMargin: profitMargin  
    };  
      
    return updatedOffer;  
  } catch (error) {        
    console.error('Error saving offer:', error);        
    return false;    
  }        
};

/**
 * Delete an offer
 * @param {string} offerName - The name of the offer to delete
 * @param {string} userId - The user ID
 * @returns {Promise<void>}
 */
export const deleteOffer = async (offerName, userId) => {    
  try {    
    const { error } = await supabase    
      .from('offers')    
      .delete()    
      .eq('user_id', userId)    
      .eq('name', offerName);

    if (error) throw error;    
    
    return true; // Return success indicator
  } catch (error) {    
    console.error('Error deleting offer:', error);    
    return false; // Return failure indicator
  }    
};

/**
 * Update allocation method for all of the user's offers
 * @param {string} allocationMethod - The new allocation method ('revenue', 'time', or 'custom')
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, count: number, error?: string}>}
 */
export const syncAllocationMethodAcrossOffers = async (allocationMethod, userId) => {
  try {
    // Safety check: Validate user ID
    if (!userId) {
      console.error('syncAllocationMethodAcrossOffers: No user ID provided');
      return { success: false, count: 0, error: 'No user ID provided' };
    }

    // Safety check: Validate allocation method
    const validMethods = ['revenue', 'time', 'custom', 'equal'];
    if (!validMethods.includes(allocationMethod)) {
      console.error('syncAllocationMethodAcrossOffers: Invalid allocation method:', allocationMethod);
      return { success: false, count: 0, error: 'Invalid allocation method' };
    }

    console.log(`Syncing allocation method to "${allocationMethod}" for user ${userId}`);

    // Perform the update - ONLY for this user's offers
    const { data, error } = await supabase
      .from('offers')
      .update({ 
        allocation_method: allocationMethod,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)  // CRITICAL: Only updates THIS user's offers
      .select();  // Returns the updated rows

    if (error) {
      console.error('syncAllocationMethodAcrossOffers: Database error:', error);
      return { success: false, count: 0, error: error.message };
    }

    const updateCount = data ? data.length : 0;
    console.log(`Successfully updated ${updateCount} offer(s)`);
    
    return { success: true, count: updateCount };
    
  } catch (error) {
    console.error('syncAllocationMethodAcrossOffers: Unexpected error:', error);
    return { success: false, count: 0, error: error.message };
  }
};

/**
 * Checks the health of the database for a specific user
 * @param {string} userId - The user ID to check
 * @returns {Promise<Object>} - Object with health status and any issues
 */
export const checkDatabaseHealth = async (userId) => {
  try {
    // Check business profiles
    const { count: profileCount, error: profileError } = await supabase
      .from('business_profiles')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
      
    if (profileError) throw profileError;
    
    // Alert if multiple profiles detected
    if (profileCount > 1) {
      console.warn(`Multiple business profiles detected (${profileCount}) for user ${userId}`);
      return {
        healthy: false,
        issues: [`Multiple business profiles (${profileCount})`]
      };
    }
    
    // Check offers consistency
    const { data: offersData, error: offersError } = await supabase
      .from('offers')
      .select('id, name')
      .eq('user_id', userId);
      
    if (offersError) throw offersError;
    
    // Check for duplicate offer names
    const offerNames = new Set();
    const duplicateNames = [];
    
    if (offersData && offersData.length > 0) {
      offersData.forEach(offer => {
        if (offerNames.has(offer.name)) {
          duplicateNames.push(offer.name);
        } else {
          offerNames.add(offer.name);
        }
      });
    }
    
    if (duplicateNames.length > 0) {
      return {
        healthy: false,
        issues: [`Duplicate offer names: ${duplicateNames.join(', ')}`]
      };
    }
    
    return { 
      healthy: true,
      summary: {
        profileCount: profileCount || 0,
        offerCount: offersData ? offersData.length : 0
      }
    };
  } catch (error) {
    console.error("Database health check failed:", error);
    return { 
      healthy: false, 
      issues: [`Check failed: ${error.message}`] 
    };
  }
};